import FormBuilderOb, {
  FormBuilderObOptions,
} from '@components/FormBuilder/FormBuilder.ob';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { Patient, usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { ErrorMessage, Loader } from '@gemini/lux-ds-pms';

import useGetCountryList from '@hooks/useGetCountryList';
import useGetCountryStatesList from '@hooks/useGetCountryStatesList';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useGetHoldersList from '@hooks/useGetHoldersList';
import useGetGenderList from '@hooks/useGetGenderList';
import { MultiplePatientsNavigator } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigator';
import { NewPatientSection } from '@components/NewPatientSection/NewPatientSection';
import { Helmet } from 'react-helmet';
import { t } from 'i18next';
import useAnalytics from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { StepContainer } from '@components/Steps/Steps.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import {
  Box,
  LuxRadioButton,
  LuxTypography,
  useTheme,
} from '@gemini/fe-ds-core';
import MultiplePatientsTabs from '@components/MultiplePatietnsTabs/MultiplePatientsTabs';
import InsuranceFormComponent from '@components/InsurancesForm/PatientDetailsInsuranceForm/PatientDetailsInsuranceForm.component';
import { InsuranceFormType } from '@components/InsurancesForm/PatientDetailsInsuranceForm/PatientDetailsInsuranceForm.interface';
import CustomInsuranceFieldsForm from '@components/InsurancesForm/CustomInsuranceFieldsForm/CustomInsuranceFieldsForm.component';
import { hasVisibleCustomInsuranceFields } from '@components/InsurancesForm/CustomInsuranceFieldsForm/CustomInsuranceFieldsForm.utils';
import {
  isPrimaryInsurancePlanRequired,
  isSecondaryInsurancePlanRequired,
  shouldShowPrimaryInsurancePlanQuestion,
  shouldShowSecondaryInsurancePlanQuestion,
} from './PatientDetailsPage.utils';

interface PatientDetailsProps {
  setEnableButton: (val: boolean) => void;
}

const getSecondaryInsurancePlanAnswer = (patient?: Patient) =>
  patient?.patientDetails?.secondaryInsuranceDetails?.submitInsurancePlan ||
  (patient?.insurance?.secondaryInsurance?.id ? StaticValues.YES : '');

const PatientDetailsPage = ({ setEnableButton }: PatientDetailsProps) => {
  // #region Hooks

  const { trackPageAnalytics } = useAnalytics();
  const theme = useTheme();

  const showAddNewPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );
  const currentPatientIndex = useOnlineBookingStore(
    state => state.currentPatient,
  );
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const brandName = useOnlineBookingStore(state => state.brandName);
  const organizationId = useOnlineBookingStore(
    state => state.generalInfo?.organizationId,
  );
  const readOnlyPrefilledPatientsDetailsFields = useOnlineBookingStore(
    state => state.readOnlyPrefilledPatientsDetailsFields,
  );
  const infoUser = useOnlineBookingStore(state => state.infoUser);

  const updatePatient = usePatientStore(state => state.updatePatient);
  const patients = usePatientStore(state => state.patients);
  const [primaryInsuranceValid, setPrimaryInsuranceValid] = useState<boolean[]>(
    [],
  );
  const [secondaryInsuranceValid, setSecondaryInsuranceValid] = useState<
    boolean[]
  >([]);
  const [customInsuranceFieldsValid, setCustomInsuranceFieldsValid] = useState<
    boolean[]
  >([]);
  const [insurancePlan, setInsurancePlan] = useState<string[]>(
    patients?.map(p => p?.insurance?.plan || '') || [],
  );
  const insurancePlanRef = useRef(insurancePlan);
  const [secondaryInsuranceAnswer, setSecondaryInsuranceAnswer] = useState<
    string[]
  >(patients?.map(getSecondaryInsurancePlanAnswer) || []);
  const secondaryInsuranceAnswerRef = useRef(secondaryInsuranceAnswer);

  const {
    data: genderList,
    isLoading: isGenderLoading,
    isError: isErrorInGender,
  } = useGetGenderList({
    organizationId,
  });

  const {
    data: holders,
    isLoading: isHoldersLoading,
    isError: isErrorInHolders,
  } = useGetHoldersList({
    organizationId,
  });

  const {
    data: countryList,
    isLoading: isCountryListLoading,
    isError: isErrorInCountryList,
  } = useGetCountryList();

  const {
    data: countryStatesList,
    isLoading: isCountryStatesListLoading,
    isError: isErrorInCountryStatesList,
  } = useGetCountryStatesList({ countryCode: 'US' });

  const [listEnableButton, setListEnableButton] = useState<
    Array<boolean | undefined>
  >([]);

  // #endregion Hooks

  // #region Memos

  const askForInsuranceInformation = useMemo(
    () => !!formConfigs?.askForInsuranceInformation,
    [formConfigs],
  );

  const insuranceDrivesAvailability = useMemo(
    () =>
      askForInsuranceInformation && formConfigs?.insuranceMustDriveAvailability,
    [askForInsuranceInformation, formConfigs],
  );

  const showInsuranceInPatientDetails = useMemo(
    () => askForInsuranceInformation && !insuranceDrivesAvailability,
    [askForInsuranceInformation, insuranceDrivesAvailability],
  );

  const primaryInsurancePlanStatus =
    formConfigs?.patientDetails?.primaryInsuranceDetails?.submitInsurancePlan;

  const showPrimaryInsurancePlanQuestion = useMemo(
    () =>
      shouldShowPrimaryInsurancePlanQuestion(
        showInsuranceInPatientDetails,
        primaryInsurancePlanStatus,
      ),
    [primaryInsurancePlanStatus, showInsuranceInPatientDetails],
  );

  const primaryInsurancePlanRequired = useMemo(
    () => isPrimaryInsurancePlanRequired(primaryInsurancePlanStatus),
    [primaryInsurancePlanStatus],
  );

  const secondaryInsurancePlanStatus =
    formConfigs?.patientDetails?.secondaryInsuranceDetails?.submitInsurancePlan;

  const showSecondaryInsurancePlanQuestion = useMemo(
    () =>
      shouldShowSecondaryInsurancePlanQuestion(secondaryInsurancePlanStatus),
    [secondaryInsurancePlanStatus],
  );

  const secondaryInsurancePlanRequired = useMemo(
    () => isSecondaryInsurancePlanRequired(secondaryInsurancePlanStatus),
    [secondaryInsurancePlanStatus],
  );

  const showCustomInsuranceFields = useMemo(
    () =>
      hasVisibleCustomInsuranceFields(
        formConfigs?.patientDetails?.customFieldsInsurance,
      ),
    [formConfigs],
  );

  const shouldShowInsuranceSection = useCallback(
    (patient?: Patient) => {
      if (!askForInsuranceInformation) {
        return false;
      }

      if (!insuranceDrivesAvailability) {
        return (
          showPrimaryInsurancePlanQuestion ||
          patient?.insurance?.plan === StaticValues.YES
        );
      }

      return patient?.insurance?.plan === StaticValues.YES;
    },
    [
      askForInsuranceInformation,
      insuranceDrivesAvailability,
      showPrimaryInsurancePlanQuestion,
    ],
  );

  const shouldShowInsuranceForms = useCallback(
    (patient: Patient, index: number) => {
      if (insuranceDrivesAvailability) {
        return patient.insurance?.plan === StaticValues.YES;
      }

      return (
        (insurancePlan[index] || patient.insurance?.plan) === StaticValues.YES
      );
    },
    [insuranceDrivesAvailability, insurancePlan],
  );

  const shouldShowSecondaryInsuranceForm = useCallback(
    (patient: Patient, index: number) => {
      const answer =
        secondaryInsuranceAnswer[index] ||
        getSecondaryInsurancePlanAnswer(patient);

      return answer === StaticValues.YES;
    },
    [secondaryInsuranceAnswer],
  );

  const enableButtonMaster = useMemo(() => {
    const patientDetailsAreComplete = listEnableButton.every(
      enable => (typeof enable === 'boolean' && enable) || enable === undefined,
    );
    const insuranceSectionsAreComplete = patients.every((patient, index) => {
      if (!patient || !shouldShowInsuranceSection(patient)) {
        return true;
      }

      const currentInsurancePlan =
        insurancePlan[index] || patient.insurance?.plan;

      if (
        showPrimaryInsurancePlanQuestion &&
        primaryInsurancePlanRequired &&
        !currentInsurancePlan
      ) {
        return false;
      }

      if (!shouldShowInsuranceForms(patient, index)) {
        return true;
      }

      if (primaryInsuranceValid[index] !== true) {
        return false;
      }

      if (
        showCustomInsuranceFields &&
        customInsuranceFieldsValid[index] !== true
      ) {
        return false;
      }

      if (!showSecondaryInsurancePlanQuestion) {
        return true;
      }

      const currentSecondaryInsuranceAnswer =
        secondaryInsuranceAnswer[index] ||
        getSecondaryInsurancePlanAnswer(patient);

      if (!currentSecondaryInsuranceAnswer) {
        return !secondaryInsurancePlanRequired;
      }

      if (currentSecondaryInsuranceAnswer === StaticValues.NO) {
        return true;
      }

      return secondaryInsuranceValid[index] === true;
    });

    return patientDetailsAreComplete && insuranceSectionsAreComplete;
  }, [
    insurancePlan,
    listEnableButton,
    customInsuranceFieldsValid,
    patients,
    primaryInsuranceValid,
    secondaryInsuranceAnswer,
    secondaryInsuranceValid,
    shouldShowInsuranceForms,
    shouldShowInsuranceSection,
    showCustomInsuranceFields,
    showPrimaryInsurancePlanQuestion,
    showSecondaryInsurancePlanQuestion,
    primaryInsurancePlanRequired,
    secondaryInsurancePlanRequired,
  ]);

  const patientDetailsFormIdsList = useMemo(() => {
    return patients.map(() => {
      const resArray = [FormIds.PatientDetail];

      if (formConfigs?.patientDetails.customFieldsPatient?.show) {
        resArray.push(FormIds.CustomFieldsPatient);
      }

      return resArray;
    });
  }, [formConfigs, patients]);

  const patientDetailsFormOptionsList = useMemo(() => {
    return patients.map((p, index) => {
      const resArray: Array<FormBuilderObOptions> = [
        {
          countryList: countryList,
          countryStatesList: countryStatesList,
          genderList: genderList,
          readOnlyPrefilledPatientsDetailsFields,
          prefilledPatientDetailsFields: {
            firstName: index === 0 && !!infoUser?.firstName,
            lastName: index === 0 && !!infoUser?.lastName,
            dateOfBirth: index === 0 && !!infoUser?.dateOfBirth,
            email: index === 0 && !!infoUser?.email,
            phoneNumber: index === 0 && !!infoUser?.phoneNumber?.number,
          },
        },
      ];
      if (formConfigs?.patientDetails.customFieldsPatient?.show) {
        resArray.push({});
      }

      return resArray;
    });
  }, [countryList, countryStatesList, formConfigs, genderList, patients]);

  const isLoading = useMemo(
    () =>
      isCountryListLoading ||
      isCountryStatesListLoading ||
      isHoldersLoading ||
      isGenderLoading,
    [
      isCountryListLoading,
      isCountryStatesListLoading,
      isGenderLoading,
      isHoldersLoading,
    ],
  );

  const isError = useMemo(
    () =>
      isErrorInCountryList ||
      isErrorInCountryStatesList ||
      isErrorInGender ||
      isErrorInHolders,
    [
      isErrorInCountryList,
      isErrorInCountryStatesList,
      isErrorInGender,
      isErrorInHolders,
    ],
  );

  const patientsPresenceKey = useMemo(
    () => patients.map(p => (p ? '1' : '0')).join('|'),
    [patients],
  );

  // #endregion Memos

  // #region Functions

  const handleEnableButton = useCallback((index: number, enable: boolean) => {
    setListEnableButton(prev => {
      if (typeof prev[index] !== 'undefined' && prev[index] !== enable) {
        // Aggiorna solamente se cambia il valore
        const newVal = [...prev];
        newVal[index] = enable;
        return newVal;
      }
      return prev; // se non cambiano i valori ritorna lo stato vecchio
    });
  }, []);

  const handleValidatePrimaryInsurance = useCallback(
    (isValid: boolean, index: number) => {
      setPrimaryInsuranceValid(prev => {
        const newPrimaryInsuranceValid = [...prev];
        newPrimaryInsuranceValid[index] = isValid;
        return newPrimaryInsuranceValid;
      });
    },
    [],
  );

  const handleValidateSecondaryInsurance = useCallback(
    (isValid: boolean, index: number) => {
      setSecondaryInsuranceValid(prev => {
        const newSecondaryInsuranceValid = [...prev];
        newSecondaryInsuranceValid[index] = isValid;
        return newSecondaryInsuranceValid;
      });
    },
    [],
  );

  const handleValidateCustomInsuranceFields = useCallback(
    (isValid: boolean, index: number) => {
      setCustomInsuranceFieldsValid(prev => {
        const newCustomInsuranceFieldsValid = [...prev];
        newCustomInsuranceFieldsValid[index] = isValid;
        return newCustomInsuranceFieldsValid;
      });
    },
    [],
  );

  // #endregion Functions

  // #region Effects

  //ANALYTICS (RONA only) - Personal Info Form
  useEffect(() => {
    if (brandName) {
      trackPageAnalytics({
        page: showAddNewPatientFrame
          ? FlowsPages.ADD_NEW_PATIENT
          : FlowsPages.PATIENT_DETAILS,
        patients,
      });
    }
  }, [showAddNewPatientFrame]);

  useEffect(() => {
    setEnableButton(enableButtonMaster);
  }, [enableButtonMaster]);

  useEffect(() => {
    setListEnableButton(patients.map(p => (p ? false : undefined)));
  }, [patientsPresenceKey]);

  // #endregion Effects

  // #region Constants

  const planList = [
    { label: 'Yes', value: StaticValues.YES },
    { label: 'No', value: StaticValues.NO },
  ];

  // #endregion Constants

  // #region JSX

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_patient_details_title')}</title>
      </Helmet>
      {showAddNewPatientFrame ? (
        <StepContainer
          borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
        >
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4" component="h2">
              {t('newPatientPage.title')}
            </LuxTypography>
          </CardTitleWrapper>
          <MultiplePatientsTabs />
          <NewPatientSection setEnableButton={setEnableButton} />
        </StepContainer>
      ) : isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorMessage></ErrorMessage>
      ) : (
        <>
          {patients.map((patient, index) =>
            patient ? (
              <Fragment key={`patientDetailsForms_${index}`}>
                <StepContainer
                  borderColor={
                    theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                  }
                  style={{
                    display: currentPatientIndex === index ? 'flex' : 'none',
                  }}
                >
                  <CardTitleWrapper
                    borderColor={
                      theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                    }
                  >
                    <LuxTypography variant="heading4" component="h2">
                      {t('patientDetailsPage.title')}
                    </LuxTypography>
                  </CardTitleWrapper>
                  <MultiplePatientsTabs />
                  <FormBuilderOb
                    key={`patientDetails_${index}`}
                    isVisible={currentPatientIndex === index}
                    formIds={patientDetailsFormIdsList[index]}
                    setEnableButton={enable =>
                      handleEnableButton(index, enable)
                    }
                    onUnmount={formData => {
                      const plan = insuranceDrivesAvailability
                        ? patient.insurance?.plan
                        : insurancePlanRef.current[index] ||
                          patient.insurance?.plan;
                      const shouldPersistSecondaryInsuranceAnswer =
                        plan === StaticValues.YES &&
                        showSecondaryInsurancePlanQuestion;
                      const secondaryAnswer =
                        shouldPersistSecondaryInsuranceAnswer
                          ? secondaryInsuranceAnswerRef.current[index] ||
                            getSecondaryInsurancePlanAnswer(patient)
                          : '';
                      const shouldClearSecondaryInsurance =
                        secondaryAnswer === StaticValues.NO;
                      const shouldPersistInsurancePlan =
                        !!plan && !insuranceDrivesAvailability;
                      const insuranceFormData =
                        plan === StaticValues.NO
                          ? {
                              plan,
                              id: '',
                              name: '',
                              other: '',
                              memberId: '',
                            }
                          : { plan };
                      const patientDetailsFormData =
                        shouldPersistInsurancePlan ||
                        shouldClearSecondaryInsurance
                          ? {
                              ...formData,
                              patientDetails: {
                                ...(formData?.patientDetails as object),
                                ...(shouldClearSecondaryInsurance
                                  ? {
                                      secondaryInsuranceDetails: undefined,
                                    }
                                  : {}),
                              },
                              ...(shouldPersistInsurancePlan
                                ? {
                                    insurance: insuranceFormData,
                                  }
                                : {}),
                            }
                          : formData;

                      updatePatient(
                        index,
                        FormIds.PatientDetail,
                        patientDetailsFormData,
                      );
                    }}
                    optionsList={patientDetailsFormOptionsList[index]}
                    patientIndex={index}
                    mykey={`patientDetails_${index}`}
                  />
                </StepContainer>
                {shouldShowInsuranceSection(patient) && (
                  <StepContainer
                    marginTop="1.5rem"
                    borderColor={
                      theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                    }
                    style={{
                      display: currentPatientIndex === index ? 'flex' : 'none',
                    }}
                  >
                    <CardTitleWrapper
                      borderColor={
                        theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                      }
                    >
                      <LuxTypography variant="heading4" component="h2">
                        {t('detailsSection.insuranceDetails')}
                      </LuxTypography>
                    </CardTitleWrapper>
                    {showPrimaryInsurancePlanQuestion && (
                      <Box padding="0.5rem 1rem 1rem 1rem">
                        <LuxRadioButton
                          name={`patientDetailsInsurancePlan_${index}`}
                          direction="horizontal"
                          required={primaryInsurancePlanRequired}
                          standalone
                          label={
                            t(
                              'fieldsLabel.primaryInsuranceDetails.insurancePlan',
                            ) as string
                          }
                          options={planList}
                          value={
                            insurancePlan[index] || patient.insurance?.plan
                          }
                          onChange={v => {
                            const newInsurancePlan = [...insurancePlan];
                            newInsurancePlan[index] = v;
                            insurancePlanRef.current = newInsurancePlan;
                            setInsurancePlan(newInsurancePlan);
                            updatePatient(index, FormIds.Insurance, {
                              insurance: { plan: v },
                              ...(v === StaticValues.NO
                                ? {
                                    patientDetails: {
                                      storeCustomFieldsInsurance: undefined,
                                    },
                                  }
                                : {}),
                            });
                            if (v === StaticValues.NO) {
                              handleValidateCustomInsuranceFields(true, index);
                            }
                            handleValidatePrimaryInsurance(
                              v === StaticValues.NO,
                              index,
                            );
                          }}
                        />{' '}
                      </Box>
                    )}

                    {shouldShowInsuranceForms(patient, index) && (
                      <>
                        <InsuranceFormComponent
                          key={`${InsuranceFormType.PRIMARY}_${index}`}
                          currentIndex={index}
                          insuranceFormType={InsuranceFormType.PRIMARY}
                          countryStates={countryStatesList}
                          countries={countryList}
                          gender={genderList}
                          holders={holders}
                          defaultInsuranceID={patient?.insurance?.id}
                          defaultInsuranceNameOther={patient?.insurance?.other}
                          onValidate={handleValidatePrimaryInsurance}
                        />
                        {showSecondaryInsurancePlanQuestion && (
                          <Box padding="0.5rem 1rem 1rem 1rem">
                            <LuxRadioButton
                              name={`patientDetailsSecondaryInsurancePlan_${index}`}
                              direction="horizontal"
                              required={secondaryInsurancePlanRequired}
                              standalone
                              label={
                                t(
                                  'fieldsLabel.secondaryInsuranceDetails.insurancePlan',
                                ) as string
                              }
                              options={planList}
                              value={
                                secondaryInsuranceAnswer[index] ||
                                getSecondaryInsurancePlanAnswer(patient)
                              }
                              onChange={v => {
                                const newSecondaryInsuranceAnswer = [
                                  ...secondaryInsuranceAnswer,
                                ];
                                newSecondaryInsuranceAnswer[index] = v;
                                secondaryInsuranceAnswerRef.current =
                                  newSecondaryInsuranceAnswer;
                                setSecondaryInsuranceAnswer(
                                  newSecondaryInsuranceAnswer,
                                );
                                handleValidateSecondaryInsurance(
                                  v === StaticValues.NO,
                                  index,
                                );
                              }}
                            />
                          </Box>
                        )}
                        {showSecondaryInsurancePlanQuestion &&
                          shouldShowSecondaryInsuranceForm(patient, index) && (
                            <InsuranceFormComponent
                              key={`${InsuranceFormType.SECONDARY}_${index}`}
                              currentIndex={index}
                              insuranceFormType={InsuranceFormType.SECONDARY}
                              countryStates={countryStatesList}
                              countries={countryList}
                              gender={genderList}
                              holders={holders}
                              defaultInsuranceID={undefined}
                              onValidate={handleValidateSecondaryInsurance}
                            />
                          )}
                        {showCustomInsuranceFields && (
                          <CustomInsuranceFieldsForm
                            key={`customInsuranceFields_${index}`}
                            currentIndex={index}
                            customFields={
                              formConfigs?.patientDetails?.customFieldsInsurance
                            }
                            initialValues={
                              patient.patientDetails?.storeCustomFieldsInsurance
                            }
                            isVisible={currentPatientIndex === index}
                            onValidate={handleValidateCustomInsuranceFields}
                            onChange={formData =>
                              updatePatient(
                                index,
                                FormIds.PatientDetail,
                                formData,
                              )
                            }
                            onCommit={formData =>
                              updatePatient(
                                index,
                                FormIds.PatientDetail,
                                formData,
                              )
                            }
                          />
                        )}
                      </>
                    )}
                  </StepContainer>
                )}
              </Fragment>
            ) : null,
          )}
        </>
      )}
      {!showAddNewPatientFrame && (
        <MultiplePatientsNavigator
          style={{ padding: '0rem 0rem 1.5rem 0rem' }}
        />
      )}
    </>
  );

  // #endregion JSX
};

export default PatientDetailsPage;
